/* ==========================================================================
   VELIORA — Hero Slider, Generic Carousel, Before/After Web Component
   ========================================================================== */
(function () {
  'use strict';

  /* ---------- Hero Slider ---------- */
  function initHeroSliders() {
    document.querySelectorAll('[data-hero-slider]').forEach((root) => {
      const slides = Array.from(root.querySelectorAll('.hero-slide'));
      const dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
      const prevBtn = root.querySelector('[data-hero-prev]');
      const nextBtn = root.querySelector('[data-hero-next]');
      const autoplay = root.getAttribute('data-autoplay') === 'true';
      const interval = parseInt(root.getAttribute('data-interval') || '6000', 10);
      let index = slides.findIndex((s) => s.classList.contains('is-active'));
      if (index < 0) index = 0;
      let timer;

      function show(newIndex) {
        slides[index].classList.remove('is-active');
        dots[index] && dots[index].classList.remove('is-active');
        index = (newIndex + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        dots[index] && dots[index].classList.add('is-active');
      }
      function next() { show(index + 1); }
      function prev() { show(index - 1); }
      function startAutoplay() {
        if (!autoplay || slides.length < 2) return;
        stopAutoplay();
        timer = setInterval(next, interval);
      }
      function stopAutoplay() { clearInterval(timer); }

      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); stopAutoplay(); startAutoplay(); }));
      if (nextBtn) nextBtn.addEventListener('click', () => { next(); stopAutoplay(); startAutoplay(); });
      if (prevBtn) prevBtn.addEventListener('click', () => { prev(); stopAutoplay(); startAutoplay(); });
      root.addEventListener('mouseenter', stopAutoplay);
      root.addEventListener('mouseleave', startAutoplay);
      root.addEventListener('focusin', stopAutoplay);
      root.addEventListener('focusout', startAutoplay);

      // Swipe
      let startX = 0;
      root.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      root.addEventListener('touchend', (e) => {
        const diff = e.changedTouches[0].clientX - startX;
        if (Math.abs(diff) > 50) { diff > 0 ? prev() : next(); stopAutoplay(); startAutoplay(); }
      }, { passive: true });

      // Keyboard
      root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') next();
        if (e.key === 'ArrowLeft') prev();
      });

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      startAutoplay();
    });
  }

  /* ---------- Generic Carousel ---------- */
  function initCarousels() {
    document.querySelectorAll('[data-slider]').forEach((root) => {
      const track = root.querySelector('.carousel__track');
      const prevBtn = root.querySelector('[data-carousel-prev]');
      const nextBtn = root.querySelector('[data-carousel-next]');
      if (!track) return;

      function scrollByAmount(dir) {
        const item = track.querySelector(':scope > *');
        if (!item) return;
        const amount = item.getBoundingClientRect().width + 24;
        track.scrollBy({ left: dir * amount, behavior: 'smooth' });
      }
      function updateButtons() {
        if (!prevBtn || !nextBtn) return;
        prevBtn.disabled = track.scrollLeft <= 4;
        nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
      }
      if (prevBtn) prevBtn.addEventListener('click', () => scrollByAmount(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => scrollByAmount(1));
      track.addEventListener('scroll', () => requestAnimationFrame(updateButtons));
      updateButtons();

      if (root.getAttribute('data-autoplay') === 'true') {
        setInterval(() => {
          if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 4) {
            track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollByAmount(1);
          }
        }, parseInt(root.getAttribute('data-interval') || '4500', 10));
      }
    });
  }

  /* ---------- Testimonial Slider ---------- */
  function initTestimonialSliders() {
    document.querySelectorAll('[data-testimonial-slider]').forEach((root) => {
      const slides = Array.from(root.querySelectorAll('.testimonial-card'));
      const dots = Array.from(root.querySelectorAll('[data-testimonial-dot]'));
      let index = 0;
      function show(i) {
        slides[index].style.display = 'none';
        index = (i + slides.length) % slides.length;
        slides[index].style.display = '';
        dots.forEach((d, di) => d.classList.toggle('is-active', di === index));
      }
      slides.forEach((s, i) => { if (i !== 0) s.style.display = 'none'; });
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
      const prevBtn = root.querySelector('[data-testimonial-prev]');
      const nextBtn = root.querySelector('[data-testimonial-next]');
      if (prevBtn) prevBtn.addEventListener('click', () => show(index - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => show(index + 1));
    });
  }

  /* ---------- Before / After custom element ---------- */
  class BeforeAfterSlider extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('.before-after__range');
      this.setPosition(50);
      this.range.addEventListener('input', () => this.setPosition(this.range.value));
      this.addEventListener('pointerdown', this.onPointerDown.bind(this));
    }
    setPosition(pct) {
      pct = Math.min(100, Math.max(0, pct));
      this.style.setProperty('--position', pct + '%');
      if (this.range) this.range.value = pct;
    }
    onPointerDown(e) {
      const move = (ev) => {
        const rect = this.getBoundingClientRect();
        const x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - rect.left;
        this.setPosition((x / rect.width) * 100);
      };
      move(e);
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    }
  }
  if (!customElements.get('before-after-slider')) customElements.define('before-after-slider', BeforeAfterSlider);

  /* ---------- Shoppable look hotspots ---------- */
  function initHotspots() {
    document.querySelectorAll('[data-shoppable-look]').forEach((root) => {
      const hotspots = root.querySelectorAll('[data-hotspot]');
      const items = root.querySelectorAll('[data-shoppable-item]');
      function activate(id) {
        items.forEach((i) => i.classList.toggle('is-active', i.getAttribute('data-shoppable-item') === id));
      }
      hotspots.forEach((hotspot) => {
        const id = hotspot.getAttribute('data-hotspot');
        hotspot.addEventListener('mouseenter', () => activate(id));
        hotspot.addEventListener('click', () => activate(id));
      });
      items.forEach((item) => {
        item.addEventListener('mouseenter', () => activate(item.getAttribute('data-shoppable-item')));
      });
    });
  }

  /* ---------- Add all to cart (shoppable look) ---------- */
  function initAddAllToCart() {
    document.querySelectorAll('[data-add-all-to-cart]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        let ids;
        try {
          ids = JSON.parse(btn.getAttribute('data-variant-ids'));
        } catch (err) {
          return;
        }
        btn.classList.add('is-loading');
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
          });
          if (!res.ok) throw new Error('add failed');
          const cart = await Veliora.fetchCart();
          Veliora.renderCart(cart);
          Veliora.toast('Added to cart');
          Veliora.openDrawer('cart-drawer');
        } catch (err) {
          Veliora.toast('Could not add items to cart', 'error');
        } finally {
          btn.classList.remove('is-loading');
        }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initHeroSliders();
    initCarousels();
    initTestimonialSliders();
    initHotspots();
    initAddAllToCart();
  });
})();
